import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Palette, Lock, AlertTriangle, Plus } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { PaintSwatch } from '../../components/nursery/LotBands';
import colourService from '../../services/nurseryColour';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useOrgTimezone, formatDateTimeInTz } from '../../utils/timezone';
import { errorMessage } from '../../utils/apiResponse';
import { cn } from '../../utils/cn';
import type {
  ColourBy, ColourHistoryRow, ColourSettings, Paint, SchemeProblem, SchemeResponse, SchemeRow,
} from '../../types/nurseryColour';

/**
 * /nursery/colour-codes — which paint colours each line gets.
 *
 * A bare-root tree has no leaves, so lines are told apart by painted bands.
 * Lines of one botanical species (peach and nectarine are one) never share a
 * colour. The scheme fills itself in; a code set by hand, or already painted
 * on live trees, is never moved by Auto-fix. Replaces hh.factree's
 * colour_coding_master and colour_refine pages
 * (docs/nursery-colour-coding-plan.md).
 */

type Tab = 'scheme' | 'paints' | 'settings' | 'history';
const TABS: { key: Tab; label: string }[] = [
  { key: 'scheme', label: 'Scheme' },
  { key: 'paints', label: 'Paints' },
  { key: 'settings', label: 'Settings' },
  { key: 'history', label: 'History' },
];

function rowName(r: SchemeRow) {
  if (r.subject_type === 'rootstock') return r.rootstock_name || 'Rootstock';
  return [r.cultivar_name, r.rootstock_name].filter(Boolean).join(' on ') || 'Cultivar';
}

const TYPE_LABEL = { cultivar: 'Cultivar', rootstock: 'Rootstock (root band)', combination: 'Cultivar on rootstock' } as const;

function problemSentence(p: SchemeProblem, rowsByKey: Map<string, SchemeRow>, settings: ColourSettings) {
  switch (p.kind) {
    case 'no_free_colour': return 'No colour is free in its species group. Add paints, or let a smaller line go without.';
    case 'retired_paint': return 'Uses a paint that has been retired.';
    case 'large_with_pair': return `More than ${settings.single_only_above.toLocaleString()} trees on a pair of colours; one colour is quicker to spot.`;
    case 'duplicate_in_group': {
      const names = (p.with ?? []).map((k) => rowsByKey.get(k)).filter(Boolean).map((r) => rowName(r as SchemeRow));
      return `Shares its colour with ${names.join(', ') || 'another line'} of the same species.`;
    }
    case 'missing_species': return 'Its cultivar has no botanical name, so it is grouped by crop type and may use colours up for nothing.';
    default: return 'Needs a look.';
  }
}

function CodeSwatches({ ids, paintById }: { ids: (number | null | undefined)[]; paintById: Map<number, Paint> }) {
  const paints = ids.filter((id): id is number => !!id).map((id) => paintById.get(id)).filter((p): p is Paint => !!p);
  if (!paints.length) return <span className="text-xs text-neutral-400">None</span>;
  return (
    <span className="inline-flex items-center gap-1 min-w-0 flex-wrap">
      {paints.map((p, i) => (
        <span key={`${p.id}-${i}`} className="inline-flex items-center gap-1 text-xs text-neutral-700">
          <PaintSwatch hex={p.hex} name={p.name} />
          <span className="break-words">{p.name}</span>
          {i < paints.length - 1 && <span className="text-neutral-400">+</span>}
        </span>
      ))}
    </span>
  );
}

// ── Scheme ──

function CodeEditor({ row, paints, onSaved, onCancel }: {
  row: SchemeRow; paints: Paint[]; onSaved: (note: string | null) => void; onCancel: () => void;
}) {
  const active = paints.filter((p) => p.active);
  const [p1, setP1] = useState<number | ''>(row.code?.paint_1_id ?? '');
  const [p2, setP2] = useState<number | ''>(row.code?.paint_2_id ?? '');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const oneBand = row.subject_type === 'rootstock';

  const save = async () => {
    if (!p1) return;
    setSaving(true);
    setError(null);
    try {
      const res = await colourService.setCode({
        subject_type: row.subject_type, cultivar_id: row.cultivar_id, rootstock_id: row.rootstock_id,
        paint_1_id: p1, paint_2_id: oneBand ? null : (p2 || null), reason: reason || null,
      });
      if (!res.success) throw new Error(errorMessage(res, 'Could not save the colour'));
      const notes: string[] = [];
      if (res.data.clashes.length) notes.push(`It now shares its colour with ${res.data.clashes.map((c) => c.name).join(', ')}.`);
      if (res.data.painted_lots_with_previous) {
        notes.push(`${res.data.painted_lots_with_previous} lot${res.data.painted_lots_with_previous === 1 ? ' is' : 's are'} painted in the old colour and will show as needing a repaint.`);
      }
      onSaved(notes.length ? notes.join(' ') : null);
    } catch (e) {
      setError(errorMessage(e, 'Could not save the colour'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 border-t border-neutral-100 pt-3 space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="text-xs text-neutral-600 min-w-0">
          {oneBand ? 'Colour' : 'Bottom band'}
          <select className="mt-0.5 w-full rounded-lg border border-neutral-300 px-2 py-1.5 text-sm" value={p1}
            onChange={(e) => setP1(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Choose</option>
            {active.map((p) => <option key={p.id} value={p.id}>{p.paint_number} · {p.name}</option>)}
          </select>
        </label>
        {!oneBand && (
          <label className="text-xs text-neutral-600 min-w-0">
            Top band (optional)
            <select className="mt-0.5 w-full rounded-lg border border-neutral-300 px-2 py-1.5 text-sm" value={p2}
              onChange={(e) => setP2(e.target.value ? Number(e.target.value) : '')}>
              <option value="">None</option>
              {active.filter((p) => p.id !== p1).map((p) => <option key={p.id} value={p.id}>{p.paint_number} · {p.name}</option>)}
            </select>
          </label>
        )}
      </div>
      <label className="block text-xs text-neutral-600">
        Why (optional, kept in the history)
        <input className="mt-0.5 w-full rounded-lg border border-neutral-300 px-2 py-1.5 text-sm" value={reason} maxLength={500}
          onChange={(e) => setReason(e.target.value)} />
      </label>
      <p className="text-xs text-neutral-500">A colour set by hand stays put: Auto-fix never moves it.</p>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={save} disabled={!p1 || saving}>{saving ? 'Saving…' : 'Save colour'}</Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
      {error && <p className="text-xs text-red-700 break-words">{error}</p>}
    </div>
  );
}

function SchemeTab({ canManage }: { canManage: boolean }) {
  const [mode, setMode] = useState<ColourBy | null>(null);
  const [scheme, setScheme] = useState<SchemeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [preview, setPreview] = useState<SchemeResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [onlyProblems, setOnlyProblems] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await colourService.getScheme(mode ?? undefined);
      if (!res.success) { setError(errorMessage(res, 'Could not load the scheme')); return; }
      setScheme(res.data);
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Could not load the scheme'));
    }
  }, [mode]);
  useEffect(() => { load(); }, [load]);

  const paintById = useMemo(() => new Map((scheme?.paints ?? []).map((p) => [p.id, p])), [scheme]);
  const rowsByKey = useMemo(() => new Map((scheme?.rows ?? []).map((r) => [r.key, r])), [scheme]);
  const problemsByKey = useMemo(() => {
    const m = new Map<string, SchemeProblem[]>();
    for (const p of scheme?.problems ?? []) m.set(p.key, [...(m.get(p.key) ?? []), p]);
    return m;
  }, [scheme]);

  if (error) return <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>;
  if (!scheme) return <div className="h-40 bg-neutral-100 rounded-xl animate-pulse" />;

  const changes = scheme.rows.filter((r) => r.proposed.action === 'assign' || r.proposed.action === 'change');
  const rows = onlyProblems ? scheme.rows.filter((r) => problemsByKey.has(r.key) || !r.code) : scheme.rows;

  const runPreview = async () => {
    setBusy(true);
    try {
      const res = await colourService.build(false, scheme.mode);
      if (!res.success) throw new Error(errorMessage(res, 'Could not work out the changes'));
      setPreview(res.data);
    } catch (e) { setError(errorMessage(e, 'Could not work out the changes')); } finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true);
    try {
      const res = await colourService.build(true, scheme.mode);
      if (!res.success) throw new Error(errorMessage(res, 'Could not save the changes'));
      setPreview(null);
      setNote(`${res.data.written} line${res.data.written === 1 ? '' : 's'} given a colour.`);
      await load();
    } catch (e) { setError(errorMessage(e, 'Could not save the changes')); } finally { setBusy(false); }
  };

  const previewChanges = preview?.rows.filter((r) => r.proposed.action === 'assign' || r.proposed.action === 'change') ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-neutral-700">
          Colour by{' '}
          <select className="ml-1 rounded-lg border border-neutral-300 px-2 py-1.5 text-sm" value={scheme.mode}
            onChange={(e) => { setMode(e.target.value as ColourBy); setPreview(null); }}>
            <option value="cultivar">Cultivar, rootstock on the root</option>
            <option value="combination">Cultivar and rootstock together</option>
          </select>
        </label>
        {scheme.mode !== scheme.settings.colour_by && (
          <span className="text-xs text-amber-800">Viewing the other scheme; Settings says the nursery uses {scheme.settings.colour_by === 'cultivar' ? 'cultivar' : 'combination'}.</span>
        )}
      </div>

      <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-2" aria-label="Summary">
        <p className="text-sm text-neutral-700">
          {scheme.rows.filter((r) => r.code).length} of {scheme.rows.length} lines have a colour
          {scheme.problems.length > 0 && ` · ${new Set(scheme.problems.map((p) => p.key)).size} need a look`}
        </p>
        {canManage && changes.length > 0 && !preview && (
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={runPreview} disabled={busy}>Auto-fix</Button>
            <span className="text-xs text-neutral-500">{changes.length} line{changes.length === 1 ? '' : 's'} would get a new colour.</span>
          </div>
        )}
        {preview && (
          <div className="border border-primary-200 bg-primary-50 rounded-lg p-3 space-y-2">
            <p className="text-sm text-neutral-800">
              {previewChanges.length ? `${previewChanges.length} line${previewChanges.length === 1 ? '' : 's'} will change:` : 'Nothing would change.'}
            </p>
            {previewChanges.length > 0 && (
              <ul className="space-y-1 text-sm">
                {previewChanges.slice(0, 30).map((r) => (
                  <li key={r.key} className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
                    <span className="font-medium break-words min-w-0">{rowName(r)}</span>
                    <span className="text-neutral-400">→</span>
                    <CodeSwatches ids={[r.proposed.paint_1_id, r.proposed.paint_2_id]} paintById={paintById} />
                  </li>
                ))}
                {previewChanges.length > 30 && <li className="text-xs text-neutral-500">and {previewChanges.length - 30} more</li>}
              </ul>
            )}
            <p className="text-xs text-neutral-600">Colours set by hand, and colours already painted on live trees, are left as they are.</p>
            <div className="flex flex-wrap gap-2">
              {previewChanges.length > 0 && <Button size="sm" onClick={apply} disabled={busy}>{busy ? 'Saving…' : 'Save these colours'}</Button>}
              <Button size="sm" variant="ghost" onClick={() => setPreview(null)}>Cancel</Button>
            </div>
          </div>
        )}
        {note && <p className="text-sm text-green-800 break-words">{note}</p>}
      </section>

      <label className="inline-flex items-center gap-2 text-sm text-neutral-700">
        <input type="checkbox" checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} />
        Only lines with no colour or a problem
      </label>

      <ul className="space-y-2">
        {rows.map((r) => {
          const probs = problemsByKey.get(r.key) ?? [];
          return (
            <li key={r.key} className={cn('bg-white border rounded-xl p-3', probs.length ? 'border-amber-200' : 'border-neutral-200')}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-neutral-900 break-words">{rowName(r)}</p>
                  <p className="text-xs text-neutral-500 break-words">
                    {TYPE_LABEL[r.subject_type]} · {r.count.toLocaleString()} trees · {r.group}
                    {r.code?.source === 'manual' && ' · set by hand'}
                    {r.painted && ' · painted'}
                  </p>
                </div>
                {canManage && editing !== r.key && (
                  <Button size="sm" variant="outline" onClick={() => { setEditing(r.key); setNote(null); }}>
                    {r.code ? 'Change' : 'Set'}
                  </Button>
                )}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                <CodeSwatches ids={[r.code?.paint_1_id, r.code?.paint_2_id]} paintById={paintById} />
                {(r.proposed.action === 'assign' || r.proposed.action === 'change') && (
                  <span className="inline-flex items-center gap-1 text-xs text-primary-700">
                    Auto-fix would make it <CodeSwatches ids={[r.proposed.paint_1_id, r.proposed.paint_2_id]} paintById={paintById} />
                  </span>
                )}
              </div>
              {probs.map((p, i) => (
                <p key={i} className="mt-1 flex items-start gap-1.5 text-xs text-amber-800">
                  <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                  <span className="break-words min-w-0">{problemSentence(p, rowsByKey, scheme.settings)}</span>
                </p>
              ))}
              {editing === r.key && (
                <CodeEditor row={r} paints={scheme.paints} onCancel={() => setEditing(null)}
                  onSaved={(n) => { setEditing(null); setNote(n ?? `${rowName(r)} saved.`); load(); }} />
              )}
            </li>
          );
        })}
      </ul>
      {rows.length === 0 && (
        <div className="bg-white border border-neutral-200 rounded-xl p-6 text-center text-sm text-neutral-500">
          {onlyProblems ? 'Every line has a colour and nothing needs a look.' : 'No lines are growing yet.'}
        </div>
      )}
    </div>
  );
}

// ── Paints ──

function PaintsTab({ canManage }: { canManage: boolean }) {
  const [paints, setPaints] = useState<Paint[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [hex, setHex] = useState('#22c55e');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await colourService.getPaints();
      if (!res.success) { setError(errorMessage(res, 'Could not load the paints')); return; }
      setPaints(res.data);
    } catch (e) { setError(errorMessage(e, 'Could not load the paints')); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await colourService.createPaint({ name: name.trim(), hex });
      if (!res.success) throw new Error(errorMessage(res, 'Could not add the paint'));
      setName('');
      await load();
    } catch (e) { setError(errorMessage(e, 'Could not add the paint')); } finally { setSaving(false); }
  };
  const toggle = async (p: Paint) => {
    try {
      const res = await colourService.updatePaint(p.id, { active: !p.active });
      if (!res.success) throw new Error(errorMessage(res, 'Could not change the paint'));
      await load();
    } catch (e) { setError(errorMessage(e, 'Could not change the paint')); }
  };

  return (
    <div className="space-y-4">
      {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>}
      <p className="text-sm text-neutral-600 break-words">
        A paint is retired, never deleted: trees already painted keep their own record of it.
      </p>
      {canManage && (
        <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-2" aria-label="Add a paint">
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-xs text-neutral-600 flex-1 min-w-[10rem]">
              Name
              <input className="mt-0.5 w-full rounded-lg border border-neutral-300 px-2 py-1.5 text-sm" value={name} maxLength={100}
                onChange={(e) => setName(e.target.value)} placeholder="e.g. Fluoro pink" />
            </label>
            <label className="text-xs text-neutral-600">
              Colour
              <input type="color" className="mt-0.5 block h-9 w-14 rounded border border-neutral-300" value={hex}
                onChange={(e) => setHex(e.target.value)} aria-label="Paint colour" />
            </label>
            <Button size="sm" onClick={add} disabled={!name.trim() || saving}><Plus className="h-4 w-4 mr-1" /> Add paint</Button>
          </div>
        </section>
      )}
      {!paints ? <div className="h-32 bg-neutral-100 rounded-xl animate-pulse" /> : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {paints.map((p) => (
            <li key={p.id} className={cn('bg-white border border-neutral-200 rounded-lg px-3 py-2 flex items-center gap-3', !p.active && 'opacity-60')}>
              <PaintSwatch hex={p.hex} name={p.name} className="h-6 w-6" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-neutral-900 break-words">{p.paint_number} · {p.name}</p>
                <p className="text-xs text-neutral-500">{p.hex}{p.active ? '' : ' · retired'}</p>
              </div>
              {canManage && (
                <Button size="sm" variant="ghost" onClick={() => toggle(p)}>{p.active ? 'Retire' : 'Use again'}</Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Settings ──

function SettingsTab({ canAdmin }: { canAdmin: boolean }) {
  const [s, setS] = useState<ColourSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    colourService.getSettings()
      .then((res) => { if (res.success) setS(res.data); else setError(errorMessage(res, 'Could not load the settings')); })
      .catch((e) => setError(errorMessage(e, 'Could not load the settings')));
  }, []);

  if (error) return <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>;
  if (!s) return <div className="h-32 bg-neutral-100 rounded-xl animate-pulse" />;

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const res = await colourService.updateSettings({
        enabled: s.enabled, colour_by: s.colour_by, no_colour_below: s.no_colour_below, single_only_above: s.single_only_above,
      });
      if (!res.success) throw new Error(errorMessage(res, 'Could not save the settings'));
      setS(res.data);
      setSaved(true);
    } catch (e) { setError(errorMessage(e, 'Could not save the settings')); } finally { setSaving(false); }
  };

  return (
    <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-4" aria-label="Settings">
      {!canAdmin && <p className="text-sm text-neutral-600">A Nursery Admin changes these.</p>}
      <fieldset disabled={!canAdmin} className="space-y-4">
        <label className="flex items-start gap-2 text-sm text-neutral-800">
          <input type="checkbox" className="mt-1" checked={s.enabled} onChange={(e) => setS({ ...s, enabled: e.target.checked })} />
          <span>
            Use colour coding
            <span className="block text-xs text-neutral-500">The scheme is kept either way; on, staff see the colours on lots, pick cards and the painting page.</span>
          </span>
        </label>
        <label className="block text-sm text-neutral-700">
          Colour by
          <select className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm" value={s.colour_by}
            onChange={(e) => setS({ ...s, colour_by: e.target.value as ColourBy })}>
            <option value="cultivar">Cultivar on the stem, rootstock on the root</option>
            <option value="combination">Cultivar and rootstock together, no root band</option>
          </select>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-sm text-neutral-700">
            No colour below
            <input type="number" min="0" className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm"
              value={s.no_colour_below} onChange={(e) => setS({ ...s, no_colour_below: Number(e.target.value) || 0 })} />
            <span className="block text-xs text-neutral-500 mt-0.5">trees; a smaller line is not given a colour.</span>
          </label>
          <label className="text-sm text-neutral-700">
            One colour above
            <input type="number" min="0" className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm"
              value={s.single_only_above} onChange={(e) => setS({ ...s, single_only_above: Number(e.target.value) || 0 })} />
            <span className="block text-xs text-neutral-500 mt-0.5">trees; a bigger line gets a single colour, not a pair.</span>
          </label>
        </div>
      </fieldset>
      {canAdmin && (
        <div className="flex items-center gap-3">
          <Button size="sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</Button>
          {saved && <span className="text-xs text-green-700">Saved.</span>}
        </div>
      )}
    </section>
  );
}

// ── History ──

function HistoryTab() {
  const tz = useOrgTimezone();
  const [rows, setRows] = useState<ColourHistoryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    colourService.history()
      .then((res) => { if (res.success) setRows(res.data); else setError(errorMessage(res, 'Could not load the history')); })
      .catch((e) => setError(errorMessage(e, 'Could not load the history')));
  }, []);
  if (error) return <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>;
  if (!rows) return <div className="h-32 bg-neutral-100 rounded-xl animate-pulse" />;
  if (!rows.length) return <p className="text-sm text-neutral-500">No changes yet.</p>;
  return (
    <ul className="space-y-2">
      {rows.map((h) => (
        <li key={h.id} className="bg-white border border-neutral-200 rounded-lg px-3 py-2 text-sm">
          <p className="font-medium text-neutral-900 break-words">
            {h.subject_type === 'rootstock' ? h.rootstock_name : [h.cultivar_name, h.rootstock_name].filter(Boolean).join(' on ')}
          </p>
          <p className="text-neutral-700 break-words">{h.old_label ?? 'No colour'} → {h.new_label ?? 'No colour'}</p>
          <p className="text-xs text-neutral-500 break-words">
            {formatDateTimeInTz(h.changed_at, tz)} · {h.source === 'auto' ? 'Automatic' : (h.changed_by_name || 'By hand')}
            {h.reason ? ` · ${h.reason}` : ''}
          </p>
        </li>
      ))}
    </ul>
  );
}

function ColourCodes() {
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find((t) => t.key === params.get('tab'))?.key ?? 'scheme') as Tab;
  const { hasAccess, loading } = useModuleLicense('nursery', ['operator', 'manager', 'admin']);
  const { hasAccess: canManage } = useModuleLicense('nursery', ['manager', 'admin']);
  const { hasAccess: canAdmin } = useModuleLicense('nursery', ['admin']);

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-6"><div className="h-40 bg-neutral-100 rounded animate-pulse" /></div>;
  if (!hasAccess) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-neutral-800 mb-1">Nursery seat required</h2>
          <p className="text-sm text-neutral-500">Colour codes need a nursery operator, manager or admin seat.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader title="Colour codes" subtitle="The paint bands that tell lines apart" icon={<Palette className="h-6 w-6 text-white" />} />
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        <div className="flex flex-wrap items-center gap-2" role="tablist">
          {TABS.map((t) => (
            <button key={t.key} type="button" role="tab" aria-selected={tab === t.key}
              onClick={() => setParams(t.key === 'scheme' ? new URLSearchParams() : new URLSearchParams({ tab: t.key }))}
              className={cn('rounded-full px-3 py-1.5 text-sm', tab === t.key ? 'bg-primary-600 text-white' : 'bg-neutral-100 text-neutral-700')}>
              {t.label}
            </button>
          ))}
          <Link to="/nursery/painting" className="ml-auto text-sm text-primary-700 underline">Painting</Link>
        </div>
        {tab === 'scheme' && <SchemeTab canManage={canManage} />}
        {tab === 'paints' && <PaintsTab canManage={canManage} />}
        {tab === 'settings' && <SettingsTab canAdmin={canAdmin} />}
        {tab === 'history' && <HistoryTab />}
      </div>
    </>
  );
}

export default ColourCodes;
